import type { DragEvent, ReactNode } from 'react'
import type { FileRejection, FileRule } from '~/helpers/files/file-rule'
import { FileArchive, Upload } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { checkFiles } from '~/helpers/files/file-rule'
import { cn } from '~/lib/utils'
import { formatBytes } from '~/utils/bytes'

type Props = {
  /** Id du champ fichier, que le `<label>` de l'appelant désigne. */
  id: string
  rule: FileRule
  /** Le fichier retenu, affiché dans la zone tant que l'appelant le garde. */
  file: File | null
  /**
   * Un fichier que `rule` accepte, ou `null` quand le nouveau est refusé : le
   * précédent ne reste pas choisi sous un message qui dit le contraire.
   */
  onFile: (file: File | null) => void
  disabled?: boolean
  /** Ce qui est attendu (format, poids), sous l'invite. */
  hint?: ReactNode
}

/**
 * Zone où glisser un fichier, ou cliquer pour le choisir (#310).
 *
 * Le champ fichier reste dans la page, masqué visuellement mais focalisable :
 * c'est lui que le clavier atteint et que le lecteur d'écran annonce, avec le
 * libellé de l'appelant. La zone n'est qu'une cible plus grande pour la souris.
 * Un fichier refusé l'est tout de suite, avec la raison, sans rien envoyer.
 */
function FileDropZone({ id, rule, file, onFile, disabled = false, hint }: Props) {
  const { t, i18n } = useTranslation()
  const [dragging, setDragging] = useState(false)
  const [rejection, setRejection] = useState<FileRejection | null>(null)
  const errorId = `${id}-error`

  const take = (files: readonly File[]) => {
    const refused = checkFiles(files, rule)
    setRejection(refused)
    onFile(refused ? null : files[0])
  }

  // `preventDefault` même désactivée : sans lui, le navigateur ouvrirait le
  // fichier lâché à la place de la page.
  const onDragOver = (event: DragEvent) => {
    event.preventDefault()
    if (disabled) {
      event.dataTransfer.dropEffect = 'none'
      return
    }
    setDragging(true)
  }
  const onDragLeave = (event: DragEvent) => {
    const into = event.relatedTarget
    if (!(into instanceof Node && event.currentTarget.contains(into))) setDragging(false)
  }
  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setDragging(false)
    // Un texte ou un lien glissé n'apporte aucun fichier : rien à refuser.
    const files = [...event.dataTransfer.files]
    if (!disabled && files.length > 0) take(files)
  }

  const rejectionMessage = (refused: FileRejection): string => {
    switch (refused.kind) {
      case 'count': return t('fileDrop.rejected.count')
      case 'type': return t('fileDrop.rejected.type', { extensions: refused.extensions.join(', ') })
      case 'size': return t('fileDrop.rejected.size', {
        size: formatBytes(refused.sizeBytes, i18n.language),
        max: formatBytes(refused.maxBytes, i18n.language),
      })
    }
  }

  return (
    // Cible de dépôt pour la souris seulement : le clavier passe par le champ fichier.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="flex flex-col gap-1.5" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      {/* Un `<label>` : le clic ouvre le sélecteur sans script, et son texte
          (invite, format, poids) s'ajoute au nom accessible du champ. */}
      <label
        htmlFor={id}
        data-testid="file-drop-zone"
        data-dragging={dragging || undefined}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-input px-4 py-6 text-center transition-colors',
          'hover:bg-muted/50 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          dragging && 'border-primary-text bg-muted',
          rejection && 'border-destructive',
          disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent',
        )}
      >
        <input
          id={id}
          type="file"
          accept={rule.extensions.join(',')}
          className="sr-only"
          disabled={disabled}
          aria-describedby={rejection ? errorId : undefined}
          aria-invalid={rejection ? true : undefined}
          onChange={(event) => {
            const files = [...(event.target.files ?? [])]
            event.target.value = ''
            if (files.length > 0) take(files)
          }}
        />
        {file
          ? <FileArchive aria-hidden className="size-6 text-primary-text" />
          : <Upload aria-hidden className="size-6 text-muted-foreground" />}
        <span className="text-sm font-medium">
          {file
            ? t('fileDrop.selected', { name: file.name, size: formatBytes(file.size, i18n.language) })
            : t('fileDrop.prompt')}
        </span>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </label>
      {rejection && (
        <p id={errorId} role="alert" className="text-sm text-destructive">{rejectionMessage(rejection)}</p>
      )}
    </div>
  )
}

export default FileDropZone
